import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// DonutChart SVG
// ---------------------------------------------------------------------------

interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

interface DonutChartProps {
  slices: DonutSlice[];
  size?: number;
  strokeWidth?: number;
  className?: string;
}

const COLORS = [
  "hsl(42 80% 57%)",
  "hsl(150 54% 51%)",
  "hsl(200 70% 50%)",
  "hsl(330 70% 55%)",
  "hsl(270 60% 60%)",
];

export function DonutChart({
  slices,
  size = 120,
  strokeWidth = 24,
  className,
}: DonutChartProps): JSX.Element {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const total = slices.reduce((s, sl) => s + sl.value, 0) || 1;

  let offset = 0;
  const arcs = slices.map((sl, i) => {
    const pct = sl.value / total;
    const len = pct * circumference;
    const arc = {
      key: i,
      color: sl.color ?? COLORS[i % COLORS.length],
      length: len,
      offset,
      pct,
    };
    offset += len;
    return arc;
  });

  return (
    <div className={cn("relative inline-flex items-center justify-center", className)}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-label="Gráfico de distribuição"
        className="-rotate-90"
      >
        {/* Anel de fundo */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth={strokeWidth}
        />
        {arcs.map((arc) =>
          arc.pct > 0 ? (
            <circle
              key={arc.key}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${arc.length} ${circumference - arc.length}`}
              strokeDashoffset={-arc.offset}
              strokeLinecap="round"
              className="transition-all duration-300"
            />
          ) : null,
        )}
      </svg>
      {/* Total no centro */}
      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums text-foreground">
        {total}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DonutLegend
// ---------------------------------------------------------------------------

interface DonutLegendProps {
  slices: DonutSlice[];
  total?: number;
  className?: string;
}

export function DonutLegend({ slices, total, className }: DonutLegendProps): JSX.Element {
  const totalValue: number = total != null ? total : 0;
  const effectiveTotal = totalValue || slices.reduce((s, sl) => s + sl.value, 0) || 1;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      {slices.map((sl, i) => {
        const pct = Math.round((sl.value / effectiveTotal) * 100);
        return (
          <div key={i} className="flex items-center gap-2">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: sl.color ?? COLORS[i % COLORS.length] }}
              aria-hidden="true"
            />
            <span className="text-xs text-foreground flex-1 truncate">{sl.label}</span>
            <span className="text-xs font-mono tabular-nums text-muted-foreground">
              {sl.value.toLocaleString("pt-BR")}
            </span>
            <span className="text-[11px] font-mono tabular-nums text-muted-foreground">
              {pct}%
            </span>
          </div>
        );
      })}
    </div>
  );
}
